import { useEffect, type RefObject } from 'react'

// Marks a sticky element data-stuck while it's pinned at its `top` (scrolled up against it), so CSS can dress it
// differently from where it sits in the page. Checked once a frame at most, on scroll and resize.
export function useStuck(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let frame = 0
    const check = () => {
      frame = 0
      el.toggleAttribute(
        'data-stuck',
        isStuck(el.getBoundingClientRect().top, getComputedStyle(el).top, window.scrollY),
      )
    }
    const soon = () => {
      if (!frame) frame = requestAnimationFrame(check)
    }
    check()
    window.addEventListener('scroll', soon, { passive: true })
    window.addEventListener('resize', soon)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', soon)
      window.removeEventListener('resize', soon)
    }
  }, [ref])
}

// Pinned: the page has scrolled and the element is up against (or, slid further up, past) the top it sticks at.
// A `top` of auto means it doesn't stick at all.
export const isStuck = (at: number, top: string, scrolled: number) => {
  const pin = parseFloat(top)
  return scrolled > 0 && Number.isFinite(pin) && at <= pin + 1
}
